import { useMemo, useState } from "react";
import { DAYS, TIMES, STATUS, STATUS_ORDER } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Heatmap over a subset of registrations.
 * Clicking a cell opens a breakdown showing which children marked green/yellow/red for that slot.
 */
export default function HeatmapGrid({ registrations }) {
  const total = registrations.length;
  const [focus, setFocus] = useState(null); // { day, time }

  const data = useMemo(() => {
    const map = {};
    DAYS.forEach((d) => {
      TIMES.forEach((t) => {
        map[`${d.key}_${t}`] = { green: [], yellow: [], red: [] };
      });
    });
    registrations.forEach((r) => {
      const disp = { ...(r.disponibilita || {}), ...(r.disponibilita_admin || {}) };
      Object.entries(disp).forEach(([k, v]) => {
        if (map[k] && (v === "green" || v === "yellow" || v === "red")) {
          map[k][v].push(r);
        }
      });
    });
    return map;
  }, [registrations]);

  const best = useMemo(() => {
    let maxGreen = 0;
    Object.values(data).forEach((c) => {
      if (c.red.length === 0 && c.green.length > maxGreen) maxGreen = c.green.length;
    });
    return maxGreen;
  }, [data]);

  if (total === 0) {
    return (
      <div className="p-8 text-center text-slate-500 text-sm">
        Seleziona almeno un iscritto per generare la heatmap.
      </div>
    );
  }

  const greenBg = (count) => {
    if (count === 0) return "bg-slate-100";
    const ratio = count / total;
    if (ratio >= 0.85) return "bg-emerald-600";
    if (ratio >= 0.65) return "bg-emerald-500";
    if (ratio >= 0.45) return "bg-emerald-400";
    if (ratio >= 0.25) return "bg-emerald-300";
    return "bg-emerald-200";
  };
  const textColor = (count) => {
    if (count === 0) return "text-slate-400";
    if (count / total >= 0.45) return "text-white";
    return "text-emerald-900";
  };

  const focusCell = focus ? data[`${focus.day}_${focus.time}`] : null;
  const focusDayLabel = focus ? DAYS.find((d) => d.key === focus.day)?.label : "";
  const notMarked = focus
    ? registrations.filter((r) => {
        const key = `${focus.day}_${focus.time}`;
        return !r.disponibilita || !(r.disponibilita[key]);
      })
    : [];

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
        Ogni cella mostra quante persone del gruppo sono <strong className="text-emerald-700">disponibili</strong> in quella fascia,
        con conteggio <strong className="text-amber-700">giallo</strong> e <strong className="text-rose-700">rosso</strong>.
        L&apos;anello arancione evidenzia le fasce migliori. <strong>Clicca una cella</strong> per vedere chi ha espresso cosa.
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-4 card-warm">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-[70px_repeat(5,minmax(0,1fr))] gap-1.5 mb-2">
            <div className="text-[10px] font-mono text-slate-400 text-right pr-1">n={total}</div>
            {DAYS.map((d) => (
              <div key={d.key} className="text-center text-xs font-bold uppercase tracking-wider text-slate-700">
                {d.label}
              </div>
            ))}
          </div>

          {TIMES.map((t) => (
            <div key={t} className="grid grid-cols-[70px_repeat(5,minmax(0,1fr))] gap-1.5 mb-1.5">
              <div className="text-[11px] font-mono font-semibold text-slate-500 text-right pr-2 flex items-center justify-end">
                {t}
              </div>
              {DAYS.map((d) => {
                const cell = data[`${d.key}_${t}`];
                const gCount = cell.green.length;
                const isBest = best > 0 && cell.red.length === 0 && gCount === best;
                return (
                  <button
                    type="button"
                    key={d.key + t}
                    data-testid={`heatmap-cell-${d.key}-${t}`}
                    onClick={() => setFocus({ day: d.key, time: t })}
                    className={cn(
                      "slot-btn relative h-14 rounded-lg flex flex-col items-center justify-center text-center px-1 cursor-pointer",
                      greenBg(gCount),
                      isBest && "ring-2 ring-orange-500 ring-offset-1"
                    )}
                    title={`${gCount} disponibili, ${cell.yellow.length} scomodi, ${cell.red.length} impossibili — clicca per dettagli`}
                  >
                    <span className={cn("text-base font-bold leading-none", textColor(gCount))}>
                      {gCount}
                    </span>
                    <div className="flex gap-1 mt-1">
                      {cell.yellow.length > 0 && (
                        <span className="text-[9px] font-bold px-1 rounded bg-amber-400 text-amber-900">
                          {cell.yellow.length}
                        </span>
                      )}
                      {cell.red.length > 0 && (
                        <span className="text-[9px] font-bold px-1 rounded bg-rose-500 text-white">
                          {cell.red.length}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-600" />
          <span className="text-slate-600">Copertura massima verde</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-300" />
          <span className="text-slate-600">Copertura parziale</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded ring-2 ring-orange-500" />
          <span className="text-slate-600">Fascia ottimale (0 rossi)</span>
        </div>
      </div>

      {/* Cell breakdown dialog */}
      <Dialog open={!!focus} onOpenChange={(o) => !o && setFocus(null)}>
        <DialogContent className="max-w-lg" data-testid="cell-breakdown-dialog">
          {focus && focusCell && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">
                  {focusDayLabel} · <span className="font-mono">{focus.time}</span>
                </DialogTitle>
                <DialogDescription>
                  Chi ha espresso cosa in questa fascia oraria (gruppo di {total}).
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {STATUS_ORDER.map((k) => {
                  const list = focusCell[k];
                  const s = STATUS[k];
                  return (
                    <section key={k} data-testid={`breakdown-${k}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={cn("w-3 h-3 rounded-full", s.bg)} />
                        <h4 className={cn("text-sm font-bold", s.text)}>
                          {s.label}
                        </h4>
                        <span className="text-xs text-slate-500 font-mono">
                          ({list.length})
                        </span>
                      </div>
                      {list.length === 0 ? (
                        <p className="text-xs text-slate-400 pl-5">—</p>
                      ) : (
                        <ul className="space-y-1 pl-5">
                          {list.map((r) => (
                            <li key={r.id} className="text-sm text-slate-700 flex items-center justify-between">
                              <span>
                                <strong>{r.figlio.nome} {r.figlio.cognome}</strong>
                              </span>
                              <span className="text-xs text-slate-400 font-mono">
                                {r.genitore.telefono}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  );
                })}

                {notMarked.length > 0 && (
                  <section data-testid="breakdown-empty">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-3 h-3 rounded-full bg-slate-300" />
                      <h4 className="text-sm font-bold text-slate-500">Non indicato</h4>
                      <span className="text-xs text-slate-500 font-mono">({notMarked.length})</span>
                    </div>
                    <ul className="space-y-1 pl-5">
                      {notMarked.map((r) => (
                        <li key={r.id} className="text-sm text-slate-500">
                          {r.figlio.nome} {r.figlio.cognome}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
